// @givmo/app-charity — the unified statement's copy + status vocabulary (CPA-WEB11).
//
// ★ WHY THIS FILE EXISTS AT ALL. Every string on this surface that contains a truth-gated word
// ("sent"/"received"/"paid", see src/copy-rail.test.ts) lives HERE rather than inline in the table
// components, for the same reason `claim-vocab.ts` and `payment-vocab.ts` exist: the copy rail anchors
// its exemptions to file:line, so scattering those literals across four table components would scatter
// four anchors that any later edit re-numbers. One module means one small, stable set of anchors, and
// it puts every status word within one screen of the rule that governs it.
//
// ★ STATUS COPY IS THE SERVER'S. The backend owns the disclosure vocabulary
// (`grant_status_label` / the unassigned label in `charity_section321_service.py`) and this module
// renders it VERBATIM. The single exception is the short SENT form: the server's grant label is
// "Sent Aug 5, 2026" (the badge in the grant row's Status cell in By grant, D-CP-55), and a donation row shows the
// same fact at row density as "Sent Aug 5" — composed from the grant's own `date_sent`, and ONLY when
// that grant's `status` is `sent`. No other status is ever re-worded, abbreviated, or synthesized: the
// statement service deliberately authors the returned label as "Returned", and both the row and the
// legend use that exact short label while the explainer below carries the detail.

import type {
  Section321StatementCoveredPeriodOut,
  Section321StatementDonationOut,
  Section321StatementGrantOut,
} from '@givmo/api-client';
import type { BadgeProps } from '@givmo/design-system';
import { grantStatusTone } from './grants-decode';

type BadgeTone = NonNullable<BadgeProps['tone']>;

// ── Dates ───────────────────────────────────────────────────────────────────────────────────────────

/**
 * A UTC-pinned date formatter for this statement, in the density the covered period can carry
 * unambiguously.
 *
 * Month + day ("Aug 5") is the row-density form the design uses, and it is the right one for the
 * realistic corpus — a charity reading a few weeks of gifts. It becomes a LIE the moment the corpus
 * spans a year boundary: three "Jul 14" rows from three different years are indistinguishable, on the
 * one screen whose whole claim is that what it says is exactly true. So the year comes back whenever
 * the SERVER's covered period crosses one, which is a fact about the data rather than a guess.
 *
 * UTC, not local: `date_sent` is an instant and the backend composes its own "Sent Aug 5, 2026" label
 * in UTC. A local-time formatter here would print a different day than the label on the parent row.
 */
export function makeStatementDateFormat(
  period: Section321StatementCoveredPeriodOut | null | undefined,
): (iso: string | null | undefined) => string {
  const withYear = spansMultipleYears(period);
  return (iso) => {
    if (!iso) return '—';
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return '—';
    // 'en-US' PINNED, not the ambient browser locale. This is the first surface that composes a
    // client-side short label ("Sent " + date) which must agree with a SERVER-composed label on the
    // parent row directly above it — and the server writes "Sent Aug 5, 2026". On an en-GB browser an
    // ambient formatter renders "Sent 3 Aug" under a parent reading "Sent Aug 3, 2026", which reads as
    // two different dates for one event.
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      ...(withYear ? { year: 'numeric' } : {}),
      timeZone: 'UTC',
    });
  };
}

/**
 * The width the identity cell boxes a donation's date at.
 *
 * That box exists so the donor starts at the same x on every row, which means it is a fixed width —
 * and a fixed width has to hold the WIDEST string {@link makeStatementDateFormat} can emit. Which
 * string that is depends on the same fact the formatter keys on: whether the covered period crosses
 * a year. Measured in the real page, at `text-s13` with `tracking-givmo` and `tabular-nums`, over
 * all twelve `month: 'short'` abbreviations ("May" is the widest in Lato, a two-digit day the widest
 * day):
 *
 *   no year    "May 30"        40.80px   →  a 56px box, ~15px clear of the donor
 *   with year  "May 30, 2026"  74.42px   →  a 90px box, ~15px clear of the donor
 *
 * ★ CPA-WEB61 IS ONE BOX FOR BOTH. A 56px box under the year-bearing form is 18.42px too narrow, so
 * on every desktop row the donor — or the em dash on a donor-less row — started inside the date and
 * painted over its tail. It reached production because the covered period of every fixture in the
 * preview harness sat inside one calendar year, so no capture, no test and no cert round had ever
 * drawn the long form (the harness's `?statement=spanning` persona is the cure for that half).
 *
 * ★ THE TWO BOXES CARRY DIFFERENT VARIANTS, AND THAT ASYMMETRY IS THE POINT. Below `@min-[988px]`
 * the identity column is a bounded, sticky one that can fall to 160px, where a grandchild row's
 * `sm:pl-[64px]` and the cell's 16px right padding leave an 80px content box:
 *
 *   56px  FITS that 80px worst case, so the no-year form keeps its fixed box at every width from
 *         `sm` — exactly as it did before this fix. A statement inside one calendar year was never
 *         the defect and must not move: `@min-[988px]` is a CONTAINER query on the table frame, not
 *         the viewport, and the frame only reaches 988px at roughly 1300px of browser (measured:
 *         viewport 1280 → frame 974; 1366 → 1060). Making the narrow box desktop-lane-only therefore
 *         un-aligned the donor column on every window narrower than that, which is most laptops.
 *   90px  DOES NOT fit it (it would spill 10px into the sticky neighbour), so the year-bearing box is
 *         desktop-lane-only. In the bounded lane that form is content-sized with the `sm:pr-8`
 *         gutter instead: the donor then sits inline 8px after the date and wraps beneath it only
 *         when the name is long and the column narrow. (Below `sm` the two always stack — that is
 *         the phone layout's `flex-col`, not this lane's behaviour.)
 */
export function statementDateBoxClass(
  period: Section321StatementCoveredPeriodOut | null | undefined,
): string {
  return spansMultipleYears(period) ? DATE_BOX_WITH_YEAR : DATE_BOX_NO_YEAR;
}

/** Literal class strings — Tailwind reads source as text, so neither may be composed. */
export const DATE_BOX_NO_YEAR = 'sm:w-[56px]';
export const DATE_BOX_WITH_YEAR = '@min-[988px]:w-[90px]';

function spansMultipleYears(
  period: Section321StatementCoveredPeriodOut | null | undefined,
): boolean {
  const start = period?.start ? new Date(period.start) : null;
  const end = period?.end ? new Date(period.end) : null;
  if (!start || !end || Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    // An unknown period is the zero-data or partially-served case. Prefer the unambiguous form:
    // showing a year that was not needed costs a little width, omitting one that was costs the truth.
    return true;
  }
  return start.getUTCFullYear() !== end.getUTCFullYear();
}

// ── The status badge on a donation row ──────────────────────────────────────────────────────────────

export interface StatusBadge {
  label: string;
  tone: BadgeTone;
}

/**
 * The status shown on ONE donation row, derived from the grant that carries it.
 *
 * `grant` is the donation's own grant block (joined on `grant_ref`, which is always set — it carries
 * the `"unassigned"` sentinel rather than going null). When the grant is missing from the response
 * entirely — a shape this build should never see — the row renders no badge at all rather than
 * guessing a status, which is the one failure mode this surface cannot have.
 */
export function donationStatusBadge(
  grant: Section321StatementGrantOut | undefined,
  formatDate: (iso: string | null | undefined) => string,
): StatusBadge | null {
  if (!grant) return null;
  const tone = grantStatusTone(grant.status);
  // The short SENT form, and ONLY on a grant whose own status says sent AND that carries the instant
  // the label is composed from. Anything else renders the server's label verbatim.
  if (grant.status === 'sent' && grant.date_sent) {
    // GD-F-T20 (D-GD-43): a grant paid by mailed check is sent from the mailing, but until the
    // charity deposits the check its rows name the in-between status, never "Sent".
    if (grant.check_mailed === true) {
      return { label: `${CHECK_MAILED_PREFIX} ${formatDate(grant.date_sent)}`, tone };
    }
    return { label: `${SENT_PREFIX} ${formatDate(grant.date_sent)}`, tone };
  }
  return { label: grant.label, tone };
}

/**
 * The one composed status word on this surface. Split out so the copy rail's exemption sits on a
 * single line whose gate is visible in the same expression: it is only ever concatenated inside the
 * `status === 'sent' && date_sent` branch above.
 */
const SENT_PREFIX = 'Sent';

/**
 * The in-between status of a grant whose check is in the mail (GD-F-T20), as the server words it
 * (`_CHECK_MAILED_LABEL` in the Section 321 service). Selected by the grant's typed `check_mailed`,
 * never by its label's text.
 */
const CHECK_MAILED_PREFIX = 'Check mailed';

// ── The "What these mean" explainer ─────────────────────────────────────────────────────────────────

export interface StatusCategory {
  /** Stable key for React + testids. */
  key: 'sent' | 'check_mailed' | 'preparing' | 'unassigned' | 'returned';
  /** The label as the server words it. Never re-worded here. */
  label: string;
  tone: BadgeTone;
  /** What the category MEANS, in product words. Never a legal claim, never a promise about timing. */
  explainer: string;
}

/**
 * The five categories the Status column's (i) explains, in the order the design draws them, with a
 * check in the mail beside the sent state it belongs to (GD-F-T20).
 *
 * ⚠ THE LABELS MUST MATCH THE SERVER'S. These five strings are the backend's disclosure vocabulary
 * (`_STATUS_LABELS`, `_CHECK_MAILED_LABEL`, `_STATEMENT_LABEL_RETURNED`, and `_LABEL_UNASSIGNED` in
 * the Section 321 service and entities): a legend whose words differ from the badges beside it
 * teaches the reader a vocabulary the product does not use. `statement-vocab.test.ts` pins each one
 * against the label the preview fixtures actually serve, or the backend's literal where they serve
 * none, so a backend re-wording fails a test here rather than silently splitting the legend from the
 * table.
 *
 * The `sent` row deliberately carries the BARE label rather than a dated one — it names the category,
 * and every dated instance of it is already on the rows below. So does `check_mailed`.
 */
export const STATUS_CATEGORIES: readonly StatusCategory[] = [
  {
    key: 'sent',
    label: 'Sent',
    tone: 'success',
    explainer: 'The money reached your bank. The date is the day it was sent.',
  },
  {
    key: 'check_mailed',
    label: 'Check mailed',
    tone: 'success',
    explainer:
      'The check is in the mail. It reaches your bank when you deposit it. The date is the day it was mailed.',
  },
  {
    key: 'preparing',
    label: 'Preparing to send',
    tone: 'neutral',
    explainer: 'Donations being gathered into your next transfer.',
  },
  {
    key: 'unassigned',
    label: 'Awaiting next transfer',
    tone: 'neutral',
    explainer: 'Recorded, but not yet part of a transfer.',
  },
  {
    key: 'returned',
    label: 'Returned',
    tone: 'warning',
    explainer: "The transfer didn't complete and came back. We follow up on these.",
  },
];

/**
 * The (i) content for a GRANT parent row, or null when the row needs none.
 *
 * A sent grant needs no explainer: its label names a bank event the charity has already seen on a
 * statement. The other three are the states a reader can misread — money that is real but has not
 * moved, money gathering into the next transfer, and a transfer that came back — so each says what it
 * means in product words. Kept here rather than in the view builder so the ONE module that owns this
 * surface's status words owns all of them, including the sentences about them.
 */
export function grantRowExplainer(status: string, isUnassigned: boolean): string | null {
  if (isUnassigned) {
    return 'These donations are recorded and counted in your totals. They are not part of a transfer yet.';
  }
  switch (status) {
    case 'returned':
      return "The transfer didn't complete and came back. We follow up on these.";
    case 'preparing':
      return 'Donations being gathered into your next transfer.';
    default:
      // Includes `sent` — and any status a later backend adds, which gets no invented explanation.
      return null;
  }
}

export const STATUS_EXPLAINER_TITLE = 'What these mean';
export const STATUS_EXPLAINER_TRIGGER_LABEL = 'What these statuses mean';

// ── Toolbar vocabulary ──────────────────────────────────────────────────────────────────────────────

export type StatementTab = 'all' | 'grant' | 'fundraiser' | 'donor';

export const STATEMENT_TABS: readonly { key: StatementTab; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'grant', label: 'By grant' },
  { key: 'fundraiser', label: 'By fundraiser' },
  { key: 'donor', label: 'By donor' },
];

export type StatementSort = 'newest' | 'oldest' | 'amount-high' | 'amount-low' | 'date-sent';

/**
 * Sort options, applied CONSISTENTLY at both levels: the chosen dimension orders the groups AND the
 * donations inside each group, so there is one mental model rather than two.
 */
export const SORT_OPTIONS: readonly { value: StatementSort; label: string }[] = [
  { value: 'newest', label: 'Newest' },
  { value: 'oldest', label: 'Oldest' },
  { value: 'amount-high', label: 'Amount (high\u2192low)' },
  { value: 'amount-low', label: 'Amount (low\u2192high)' },
  { value: 'date-sent', label: 'Date sent' },
];

export type StatementChip = 'designation' | 'sent' | 'preparing';

/**
 * The three filter chips. "Has designation" is the high-value one — it is how a charity finds the
 * restricted gifts it has to track in separate books.
 */
export const CHIP_OPTIONS: readonly { value: StatementChip; label: string }[] = [
  { value: 'designation', label: 'Has designation' },
  { value: 'sent', label: 'Sent' },
  // NOT "Preparing": the predicate below is deliberately the complement of sent, so it also returns
  // RETURNED legs — whose badge reads "Returned". A chip labelled "Preparing" would then name a row
  // the Status legend defines as something else, on the same screen. "Not yet sent" is true of every
  // leg the chip selects.
  { value: 'preparing', label: 'Not yet sent' },
];

/**
 * `sent` and `preparing` partition one field (a leg is sent or it is not), so a selection carrying
 * both matches nothing. The page treats them as a radio-like pair and the URL parser drops a
 * contradictory pair, so the ledger never shows a false "0 donations" (cert R2-B-1).
 */
export const STATUS_CHIP_PAIR: readonly StatementChip[] = ['sent', 'preparing'];

/** Does this donation pass the given chip? Each reads a field the server already emitted. */
export function donationMatchesChip(
  donation: Section321StatementDonationOut,
  chip: StatementChip,
): boolean {
  switch (chip) {
    case 'designation':
      return typeof donation.designation === 'string' && donation.designation.trim().length > 0;
    case 'sent':
      return donation.sent === true;
    case 'preparing':
      // The complement of sent, in the charity's words: everything still on its way, whether it is in
      // the open accumulator or not yet in a transfer at all. Deliberately NOT `status === preparing`
      // — the chip's job is "show me what has not gone out", and a returned grant's money has not.
      return donation.sent !== true;
  }
}

export const SEARCH_PLACEHOLDER = 'Search donors, designations, fundraisers';
export const CLEAR_SEARCH_LABEL = 'Clear search';
export const COLLAPSE_ALL_LABEL = 'Collapse all';
export const EXPAND_ALL_LABEL = 'Expand all';
export const DOWNLOAD_LABEL = 'Download spreadsheet';
export const UNSHARED_GROUP_LABEL = 'Details not shared';
export const DIRECT_BUCKET_LABEL = 'Direct donations';

/**
 * The identity of the not-yet-in-a-grant bucket in By grant. The donations the server carries under
 * the `"unassigned"` sentinel have no grant record, so that row cannot honestly be named "Grant"
 * (D-CP-55: a grant is "donations grouped together into one transaction that gets sent to the
 * charity") — plan 18 §3.1 names it "Not yet designated". Its Status cell carries the server's
 * "Awaiting next transfer" badge and explainer exactly as a grant row's does.
 */
export const UNASSIGNED_GRANT_LABEL = 'Not yet designated';

/**
 * The choice-neutral explainer for the unshared bucket. It absorbs donors who DID choose to share and
 * were suppressed by the minor-protection backstop, so the copy can never say "chose not to share" —
 * that would be false for that class, and this bucket has to stay money-complete to hold the
 * partition together.
 */
export const UNSHARED_EXPLAINER =
  'Some donations do not come with a donor name or email. Their amounts are counted in every total ' +
  'on this page.';

/**
 * The same (i) while the organization has not accepted the Charity Terms (CPA-BE84). Then the bucket
 * holds EVERY donation, and the reason is the organization's, not the donors': "Some donations…" would
 * contradict the line above the table. It is still choice-neutral: it names the organization's act,
 * never a donor's.
 */
export const UNSHARED_EXPLAINER_TERMS_NOT_ACCEPTED =
  'Donor names and emails appear once your organization accepts the Charity Terms. Their amounts ' +
  'are counted in every total on this page.';

export const UNSHARED_SECONDARY_SUFFIX = 'included in every total above';

/**
 * By donor's line while the organization has not accepted the Charity Terms (CPA-BE84; plan 18 §3.3).
 * The statement read serves only the flag, so these words are the console's. The consenting-donor
 * report's sentence names "the donor-data terms", and beside a link to "the Charity Terms" that read as
 * two documents in the cut-1025d user test. The report, the CRM and the workbook keep that sentence.
 */
export const DONOR_CONSENT_LINE =
  'Donor details become available once your organization accepts the Charity Terms.';

/**
 * Added for anyone who cannot accept: a Member, or an app's Owner, whose org-level role is the wire's
 * Member. It names the org-level role as the console does (CPA-WEB94), so the reader knows who to ask.
 */
export const DONOR_CONSENT_ACCEPTER_LINE = 'A Global Admin of your organization can accept them.';

/**
 * The line's link for a Global Admin. These are the words the CRM's Terms link already uses
 * (`crm-vocab.ts`), so the console names that one door one way.
 */
export const TERMS_LINK_LABEL = 'Review the Charity Terms';

/** The same link for anyone who cannot accept: the Terms page offers them the document to read. */
export const TERMS_READ_LINK_LABEL = 'Read the Charity Terms';

/** The column header set, per tab. Keyed so the table and its tests read from one place. */
export const FIRST_COLUMN_LABEL: Record<StatementTab, string> = {
  all: 'Donation',
  grant: 'Grant',
  fundraiser: 'Fundraiser',
  donor: 'Donor',
};

// Phone-specific chrome. The accessible search name above stays descriptive; the visible placeholder
// must fit a narrow field without clipping. (The former scroll hint is gone: the next column peeking
// at the lane's edge is the pan's affordance — cert R2-A-3.)
export const SEARCH_VISIBLE_PLACEHOLDER = 'Search donations';
